import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Container from '@/components/Container';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FieldError from '@/components/FieldError';
import { verifyMfa } from '@/services/auth/auth.service';
import { useCompleteLogin } from '../useCompleteLogin';
import { useMfaTexts } from '../mfaTexts';

/**
 * Second-factor prompt for an already-enrolled user. The short-lived `mfaToken`
 * arrives via router state (set by the login screen); if it is missing (e.g. a
 * page refresh) we bounce back to /login. Copy comes from the CMS with code
 * defaults as a fallback (see mfaTexts.ts).
 */
export default function MfaVerify() {
  const location = useLocation();
  const mfaToken = (location.state as { mfaToken?: string } | null)?.mfaToken;
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const completeLogin = useCompleteLogin();
  const navigate = useNavigate();
  const t = useMfaTexts();
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  // The short-lived challenge token expired (or is invalid): the user must sign
  // in again. Distinguished from a wrong code, which they can simply retry.
  const isSessionError = (msg?: string | null) => !!msg && /session/i.test(msg);

  if (!mfaToken) return <Navigate to="/login" replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { data, error: err } = await verifyMfa(mfaToken, code.trim());
    setLoading(false);
    if (err || !data?.token) {
      if (isSessionError(err?.message)) {
        setSessionExpired(true);
        return;
      }
      setError(err?.message || t.verifyErrorFallback);
      return;
    }
    await completeLogin(data);
  };

  // Challenge session expired — dead-end with a clear message + re-login.
  if (sessionExpired) {
    return (
      <Container className="h-full">
        <div className="col-span-12 flex min-h-0 items-center justify-center py-4">
          <div className="flex max-h-[90dvh] w-full max-w-[430px] flex-col items-start overflow-auto rounded-2xl bg-white p-8 shadow-lg">
            <h2 className="mb-2 font-haffer text-sh-30-38 font-bold leading-9 tracking-tight text-black-200">
              {t.verifyTitle}
            </h2>
            <p className="mb-6 font-haffer text-sh-sm text-dark-grey">{t.verifyExpired}</p>
            <Button type="button" theme="solid" showChevron={false} onClick={() => navigate('/login')}>
              {t.backToSignInCta}
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="h-full">
      <div className="col-span-12 flex min-h-0 items-center justify-center py-4">
        <div className="flex max-h-[90dvh] w-full max-w-[430px] flex-col items-start overflow-auto rounded-2xl bg-white p-8 shadow-lg">
          <h2 className="mb-2 font-haffer text-sh-30-38 font-bold leading-9 tracking-tight text-black-200">
            {t.verifyTitle}
          </h2>
          <p className="mb-8 font-haffer text-sh-sm text-dark-grey">{t.verifyDescription}</p>

          <form className="flex w-full flex-col items-start" onSubmit={onSubmit}>
            <div className="mb-6 w-full">
              <Label>
                <p className="mb-2 font-haffer text-sh-xs text-dark-grey">{t.verifyCodeLabel}</p>
              </Label>
              <Input
                ref={codeInputRef}
                inputMode="text"
                autoComplete="one-time-code"
                placeholder={t.codePlaceholder}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              {error && <FieldError message={error} />}
            </div>

            <Button type="submit" theme="solid" className="mb-4" showChevron={false} disabled={loading}>
              {loading ? t.loadingLabel : t.verifyCta}
            </Button>
          </form>

          <Link
            className="mb-4 text-center font-haffer text-sh-xs font-bold text-people-teal-600 hover:!text-people-blue-600"
            to="/login/mfa/reset"
            state={{ mfaToken }}>
            {t.resetLinkCta}
          </Link>

          <Link
            className="text-center font-haffer text-sh-xs font-bold text-people-teal-600 hover:!text-people-blue-600"
            to="/login">
            {t.backToSignInCta}
          </Link>
        </div>
      </div>
    </Container>
  );
}
